import { useEffect, useCallback, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  GetCurrentUserDocument,
  TipsterStatus,
  UserPermission,
  UserRoles,
  UserStatus,
} from '@app/graphql'
import { USER_NO_PROFILE_PNG_IMAGE_SRC } from '@app/constants'
import { CurrentUserState, PrivateChannelState, PublicChannelState } from '@app/storage'
import { restoreAuthQueryParams, logOut, getCurrentUser, isAuthSessionGone } from '@app/helpers/auth'
import { UserCacheHelper, CachedUserData } from '@app/helpers/userCache'
import { onAuthChange } from '@app/firebase'
import { posthogIdentify } from '@app/helpers/misc'
import { LINK_TELEGRAM_LOGIN_MUTATION, linkPendingTelegramLogin } from '@app/helpers/telegramLink'

const populateUserStateFromCache = (cachedData: CachedUserData) => {
  PublicChannelState.adminJoinDisabled = false

  const permsSet = new Set(cachedData.permissions)

  const userMuted = cachedData.role === UserRoles.User && cachedData.userStatus === UserStatus.Muted
  const userBanned =
    cachedData.role === UserRoles.User && cachedData.userStatus === UserStatus.Banned
  const tipsterBanned =
    cachedData.role === UserRoles.Tipster && cachedData.tipsterStatus === TipsterStatus.Inactive

  if (userBanned || tipsterBanned) {
    CurrentUserState.banned = true
    return
  }

  CurrentUserState.authBySocial = true
  CurrentUserState.isTipsterAdmin = cachedData.isTipsterAdmin
  CurrentUserState.muted = userMuted
  CurrentUserState.onboardingStage = cachedData.onboardingStage as any
  CurrentUserState.birthday = cachedData.dateOfBirth ? new Date(cachedData.dateOfBirth) : null
  CurrentUserState.countryId = cachedData.countryId
  CurrentUserState.id = cachedData.id
  CurrentUserState.name = cachedData.firstName
  CurrentUserState.phone = cachedData.phoneNumber
  CurrentUserState.surname = cachedData.lastName
  CurrentUserState.userEmail = cachedData.emailAddress
  CurrentUserState.incognitoMode = cachedData.role == UserRoles.Guest
  CurrentUserState.userType = (
    cachedData.role === UserRoles.Guest && localStorage.getItem('onboardingFlow')
      ? UserRoles.Tipster
      : cachedData.role
  ) as UserRoles
  CurrentUserState.username = cachedData.displayName
  CurrentUserState.avatarUrl = cachedData.avatarUrl ?? USER_NO_PROFILE_PNG_IMAGE_SRC
  CurrentUserState.profileBackgroundUrl = cachedData.background
  CurrentUserState.gender = cachedData.gender as any
  CurrentUserState.bio = cachedData.bio
  CurrentUserState.unitShare = cachedData.unitShare
  CurrentUserState.unitValue = cachedData.unitValue
  CurrentUserState.permAddPost = permsSet.has(UserPermission.AddPost)
  CurrentUserState.permEditPost = permsSet.has(UserPermission.EditPost)
  CurrentUserState.ownedChannels =
    cachedData?.channelsWhereUserIsTipster?.map(({ id, logo, channelName }) => ({
      id,
      channelName,
      logo,
    })) ?? []
  CurrentUserState.followedChannels = cachedData?.followedChannels ?? []
  CurrentUserState.favoriteTenantBookieId = cachedData.favoriteTenantBookieId ?? null
  CurrentUserState.hasPendingSubscription = cachedData.hasPendingSubscription ?? false

  if (cachedData?.onboardingProgress) {
    const { __typename, ...data } = cachedData.onboardingProgress ?? {}
    CurrentUserState.tourStage = data
    PrivateChannelState.channelsList = cachedData?.followedChannels ?? []
  }

  restoreAuthQueryParams()
}
// This still needs rework there is no need to fetch user each time
export const useCurrentUser = (auto = true) => {
  const [loading, setLoading] = useState(true)
  const [getUser, { data: userData, loading: userLoading, error: userError }] = useLazyQuery(
    GetCurrentUserDocument,
    // errorPolicy 'all' so a single failing field (e.g. hasPendingSubscription)
    // doesn't discard the whole response and block the rest of the user data
    // from populating CurrentUserState.
    { fetchPolicy: 'cache-first', errorPolicy: 'all' },
  )
  const [ linkTelegramLogin ] = useMutation(LINK_TELEGRAM_LOGIN_MUTATION)

  const fetchCurrentUser = useCallback(async () => {
    await getUser()
  }, [getUser])

  useEffect(() => {
    const getUserInfo = async () => {
      const { data } = await getCurrentUser()
      if (auto) {
        const cachedUser = UserCacheHelper.load()
        if (cachedUser) {
          populateUserStateFromCache(cachedUser)
          setLoading(false)
          CurrentUserState.userDataLoading = false
        }
        // Only fetch from server when the user has an active session.
        // Unauthenticated loads (e.g. iframe before SSO fires) would otherwise
        // hit auth-guarded endpoints and produce noisy 401/403 errors.
        if ( data || sessionStorage.getItem('incognitoMode')) {
          void fetchCurrentUser()
        } else {
          setLoading(false)
          CurrentUserState.userDataLoading = false
        }
      }
    }
    getUserInfo()
  }, [auto, fetchCurrentUser])

  // Re-fetch the current user whenever Firebase signs someone in (SSO, magic link).
  useEffect(() => {
    if (!auto) return
    const unsubscribe = onAuthChange((firebaseUser) => {
      if (firebaseUser) {
        void fetchCurrentUser()
      }
    })
    return () => unsubscribe()
  }, [auto, fetchCurrentUser])

  const initUserState = useCallback(async () => {
    try {
      PublicChannelState.adminJoinDisabled = false

      if (!userData?.userCurrent) return

      const user = userData.userCurrent

      const permsSet = new Set(user.permissions)

      const userMuted = user.role === UserRoles.User && user.userStatus === UserStatus.Muted
      const userBanned = user.role === UserRoles.User && user.userStatus === UserStatus.Banned
      const tipsterBanned =
        user.role === UserRoles.Tipster && user.tipsterStatus === TipsterStatus.Inactive

      if (userBanned || tipsterBanned) {
        CurrentUserState.banned = true
        return
      }
      CurrentUserState.authBySocial = true

      CurrentUserState.muted = userMuted
      CurrentUserState.onboardingStage = user.onboardingStage
      CurrentUserState.isTipsterAdmin = user.isTipsterAdmin
      CurrentUserState.birthday = user.dateOfBirth ? new Date(user.dateOfBirth) : null
      CurrentUserState.countryId = user.countryId
      CurrentUserState.id = user.id
      CurrentUserState.name = user.firstName
      CurrentUserState.phone = user.phoneNumber
      CurrentUserState.surname = user.lastName
      CurrentUserState.userEmail = user.emailAddress
      CurrentUserState.incognitoMode = user.role == UserRoles.Guest
      CurrentUserState.userType = (
        user.role === UserRoles.Guest && localStorage.getItem('onboardingFlow')
          ? UserRoles.Tipster
          : user.role
      ) as UserRoles
      CurrentUserState.username = user.displayName
      CurrentUserState.avatarUrl = user.avatarUrl ?? USER_NO_PROFILE_PNG_IMAGE_SRC
      CurrentUserState.profileBackgroundUrl = user.background
      CurrentUserState.gender = user.gender
      CurrentUserState.bio = user.bio
      CurrentUserState.unitShare = user.unitShare
      CurrentUserState.unitValue = user.unitValue
      CurrentUserState.permAddPost = permsSet.has(UserPermission.AddPost)
      CurrentUserState.permEditPost = permsSet.has(UserPermission.EditPost)
      CurrentUserState.ownedChannels =
        user?.channelsWhereUserIsTipster?.map(({ id, logo, channelName }) => ({
          id,
          channelName,
          logo,
        })) ?? []
      CurrentUserState.followedChannels = user?.followedChannels ?? []
      CurrentUserState.favoriteTenantBookieId = user?.favoriteTenantBookieId ?? null
      CurrentUserState.hasPendingSubscription = userData?.hasPendingSubscription ?? false

      if (user?.onboardingProgress) {
        const { __typename, ...data } = user.onboardingProgress ?? {}
        CurrentUserState.tourStage = data
        PrivateChannelState.channelsList = user?.followedChannels ?? []
      }

      restoreAuthQueryParams()

      // Identify the user in PostHog as soon as their profile is loaded.
      // This runs on every page load and after every auth callback — PostHog
      // deduplicates: if the same distinct_id is already active in this
      // browser session, subsequent calls are silently ignored.
      // We always use the internal DB user ID, never the email, so this works
      // even for SSO users (e.g. Mond Casino) who may not share their email.
      posthogIdentify({
        id: user.id,
        email: user.emailAddress ?? null,
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        username: user.displayName ?? null,
      })

      UserCacheHelper.save(userData)

      // The user confirmed their email to connect a Telegram login that signup rejected (TR-837).
      if (user.role !== UserRoles.Guest) {
        void linkPendingTelegramLogin(linkTelegramLogin)
      }
    } finally {
      // The backend sends USER_NOT_REGISTERED when the token is valid but the
      // user has no account yet. Any other error (network, forbidden, guest
      // incognito) must not trigger the onboarding redirect.
      CurrentUserState.userDataLoading = false
      const couldNotResolveUser =
        userError?.graphQLErrors?.some((e) => e.extensions?.code === 'USER_NOT_REGISTERED') ?? false
      if (couldNotResolveUser) {
        CurrentUserState.initiateOnboarding = true
      } else if (userError && !userData?.userCurrent) {
        // With errorPolicy 'all', a partial failure (e.g. one field erroring)
        // still populates userData alongside userError — only log out when
        // there's no usable user data at all. Even then, a failing or
        // restarting backend is no reason to log out, so check that the
        // sign-in session itself is gone first.
        void isAuthSessionGone().then((gone) => {
          if (gone) logOut()
        })
      }
      setLoading(false)
    }
  }, [ userData, userError, userLoading, linkTelegramLogin ])

  useEffect(() => {
    initUserState()
  }, [initUserState])

  return {
    fetchCurrentUser,
    userLoading: loading || userLoading,
  }
}
